import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ICommand } from '../../../core/commands/ICommand';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import { useDiagramStore } from '../../../stores/diagramStore';
import type { OrgChartEditBatchIntent } from '../models/OrgChartEditBatchIntent';
import type { PositionStaffingResult } from '../models/PositionStaffingIntent';
import { atlasCommandContribution } from '../services/atlasCommandContribution';
import { departmentDraftMessage, scenarioDeltaToPositionIntents } from '../services/scenarioDeltaToPositionIntents';
import { scenarioDeltaToFieldIntents, withoutScenarioFieldUpdates } from '../services/scenarioDeltaToFieldIntents';
import { toIntent } from '../services/commitScenarioDelta';
import { buildPeopleHierarchy } from '../services/buildPeopleChartScene';
import { useOrgPlanningScenarioStore, type SelectedOrgPlanningScenario } from '../stores/orgPlanningScenarioStore';
import { createOrgPlanningDraftCommandPort, type DraftHistoryAction } from './createOrgPlanningDraftCommandPort';
import { useOrgModel } from './useOrgModel';
import { PositionCommandSettlementError, postPositionIntentWithReconciliation } from './positionCommandSettlement';
import { captureOrgPlanningReviewScope, isOrgPlanningReviewScopeCurrent } from './useOrgPlanningReviewScope';
import { useOrgPlanningReviewScope } from './useOrgPlanningReviewScope';
import { orgPlanningScenarioProjectionKey, type OrgPlanningScenarioProjection } from './useOrgPlanningScenarioProjection';

export function useAtlasCanvasDraftCommands(input: {
  folderId?: string | null; diagramId?: string | null; enabled: boolean; planning: boolean;
}) {
  const folderId = input.folderId ?? '';
  const diagramId = input.diagramId ?? '';
  const queryClient = useQueryClient();
  const request = useAuthenticatedFetch();
  const modelQuery = useOrgModel({ folderId, diagramId, enabled: input.enabled });
  const reviewScope = useOrgPlanningReviewScope(folderId, diagramId);
  const isDraftProjection = useCallback((result: OrgPlanningScenarioProjection | undefined, draft: SelectedOrgPlanningScenario): result is OrgPlanningScenarioProjection =>
    result?.headChangeSetId === draft.headChangeSetId && result.scenarioId === draft.scenarioId
      && result.ownerDiagramId === diagramId && result.targetFolderId === folderId && result.complete === true,
  [diagramId, folderId]);
  const projectionQuery = useCallback((draft: SelectedOrgPlanningScenario) => ({
    queryKey: ['atlas', 'organization-plan-projection', folderId, diagramId, draft.scenarioId,
      draft.headChangeSetId, 'position', reviewScope.principalId, reviewScope.actorOrgId,
      reviewScope.sessionGeneration, reviewScope.folderGeneration],
    staleTime: Infinity,
    queryFn: async ({ signal }: { signal: AbortSignal }): Promise<OrgPlanningScenarioProjection> => {
      const response = await request(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${draft.headChangeSetId}/projection?kind=position`, { signal });
      if (!response.ok) throw new PositionCommandSettlementError('http_error', response.status);
      const result = await response.json() as OrgPlanningScenarioProjection;
      if (!isDraftProjection(result, draft)) throw new PositionCommandSettlementError('http_error', 502, 'malformed_response');
      return result;
    },
  }), [diagramId, folderId, isDraftProjection, request, reviewScope.actorOrgId, reviewScope.folderGeneration,
    reviewScope.principalId, reviewScope.sessionGeneration]);
  const canvasProjection = useCallback(async (draft: SelectedOrgPlanningScenario) => {
    for (const kind of ['people', 'position'] as const) {
      const query = queryClient.getQueryCache().find<OrgPlanningScenarioProjection>({ exact: true,
        queryKey: orgPlanningScenarioProjectionKey(folderId, diagramId, draft.scenarioId, draft.headChangeSetId, kind) });
      const result = query?.state.data ?? (query?.state.fetchStatus === 'fetching'
        ? await query.promise?.catch(() => undefined) : undefined);
      if (isDraftProjection(result, draft)) return result;
    }
    return undefined;
  }, [diagramId, folderId, isDraftProjection, queryClient]);
  const mutation = useMutation({
    mutationFn: async ({ intent, signal }: { intent: OrgChartEditBatchIntent; signal: AbortSignal }): Promise<PositionStaffingResult> => {
      const scope = captureOrgPlanningReviewScope(folderId, diagramId);
      const response = await postPositionIntentWithReconciliation(() => {
        if (!isOrgPlanningReviewScopeCurrent(scope)) throw new DOMException('Access changed', 'AbortError');
        return request(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/${input.planning ? 'scenario-edit-batches' : 'implicit-chart-edit-batches'}?include=projection`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(intent), signal });
      }, () => undefined);
      const payload = await response.json().catch(() => null) as PositionStaffingResult & { error?: string } | null;
      if (!response.ok || !payload) throw new PositionCommandSettlementError('http_error', response.status, payload?.error);
      return payload;
    },
  });
  const history = useMutation({
    mutationFn: async ({ head, action, signal, restoreChangeSetId }: { head: string; action: DraftHistoryAction; signal: AbortSignal; restoreChangeSetId?: string }) => {
      const response = await request(`/api/folders/${folderId}/diagrams/${diagramId}/atlas/organization-plans/${head}/${action}`,
        { method: 'POST', signal, ...(restoreChangeSetId ? {
          headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ restoreChangeSetId }),
        } : {}) });
      const payload = await response.json().catch(() => null) as { id?: string; error?: string } | null;
      if (!response.ok || !payload?.id) throw new PositionCommandSettlementError('http_error', response.status, payload?.error ?? 'malformed_response');
      return payload.id;
    },
  });
  const primeProjection = useCallback((result: PositionStaffingResult) => {
    const { projection, changeSetId, scenarioId } = result;
    if (!projection || !changeSetId || !scenarioId) return;
    const draft = { scenarioId, headChangeSetId: changeSetId };
    if (!isDraftProjection(projection, draft)) return;
    for (const kind of ['people', 'position'] as const) {
      queryClient.setQueryData(orgPlanningScenarioProjectionKey(folderId, diagramId, scenarioId, changeSetId, kind),
        { ...projection, projectionKind: kind });
    }
  }, [diagramId, folderId, isDraftProjection, queryClient]);
  const { mutateAsync: save } = mutation;
  const { mutateAsync: transition } = history;
  const loadModel = useCallback(async () => {
    const draft = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId];
    if (!draft) return modelQuery.data;
    if (!isOrgPlanningReviewScopeCurrent(reviewScope)) return undefined;
    const projection = await canvasProjection(draft)
      ?? await queryClient.fetchQuery(projectionQuery(draft)).catch(() => undefined);
    return isOrgPlanningReviewScopeCurrent(reviewScope) ? projection?.model : undefined;
  }, [canvasProjection, diagramId, modelQuery.data, projectionQuery, queryClient, reviewScope]);
  return useCallback(async (original: ICommand): Promise<ICommand> => {
    if (original.canExecute && !original.canExecute()) throw new Error('This edit is not currently available.');
    const model = await loadModel();
    if (!model || !folderId || !diagramId || !model.completeness.complete) throw new Error('The organization must finish loading before saving this edit.');
    const delta = atlasCommandContribution(original);
    if (!delta) throw new Error('This gesture cannot be saved as an organization change. No changes were made.');
    const nodes = new Map(useDiagramStore.getState().nodes.map((node) => [node.id, node]));
    const byPerson = buildPeopleHierarchy(model).positionByPersonId;
    const positions = new Map(model.positions.map((position) => [position.id, position]));
    const people = new Set(model.people.map((person) => person.id));
    const fieldTranslation = scenarioDeltaToFieldIntents({
      delta,
      recordIdByNodeId: (id) => {
        const node = nodes.get(id);
        const attrs = node?.attrs ?? {};
        const binding = (node?.props?.dataBinding ?? attrs.dataBinding) as { recordId?: string } | undefined;
        const value = attrs.folderRecordId ?? binding?.recordId ?? attrs.personId ?? attrs.externalId;
        return typeof value === 'string' ? value : undefined;
      },
      isPersonRecord: (id) => people.has(id),
    });
    const positionTranslation = scenarioDeltaToPositionIntents({ delta: withoutScenarioFieldUpdates(delta),
      recordIdByNodeId: (id) => {
        const node = nodes.get(id);
        const attrs = node?.attrs ?? {};
        const binding = (node?.props?.dataBinding ?? attrs.dataBinding) as { recordId?: string } | undefined;
        const value = attrs.folderRecordId ?? binding?.recordId ?? attrs.personId ?? attrs.externalId;
        return typeof value === 'string' ? value : undefined;
      },
      positionByRecordId: (id) => {
        const position = positions.get(id) ?? byPerson.get(id);
        return position ? { id: position.id, title: position.label } : undefined;
      },
      isPersonRecord: (id) => people.has(id),
    });
    const unsupported = [...positionTranslation.unsupported, ...fieldTranslation.unsupported];
    const intents = [...positionTranslation.intents, ...fieldTranslation.intents];
    const departmentMessage = departmentDraftMessage(unsupported);
    if (departmentMessage) throw new Error(departmentMessage);
    if (unsupported.length || !intents.length || intents.length > 32) {
      throw new Error('This complete gesture cannot be saved safely. No part was applied.');
    }
    useOrgPlanningScenarioStore.getState().beginEditing(diagramId, input.planning ? 'scenario' : 'live');
    const selected = useOrgPlanningScenarioStore.getState().selectedByDiagram[diagramId];
    const effectiveAt = model.asOf;
    const initial: OrgChartEditBatchIntent = { kind: 'edit_batch', mode: 'scenario', intentId: crypto.randomUUID(),
      ownerDiagramId: diagramId, expectedGraphVersion: model.graphVersion, effectiveAt,
      scenarioId: selected?.scenarioId, scenarioChangeSetId: selected?.headChangeSetId,
      expectedHeadChangeSetId: selected?.headChangeSetId,
      intents: intents.map((core) => toIntent(core, { intentId: crypto.randomUUID(),
        ownerDiagramId: diagramId, expectedGraphVersion: model.graphVersion, effectiveAt,
        scenarioId: selected?.scenarioId, head: selected?.headChangeSetId })),
    };
    const port = createOrgPlanningDraftCommandPort({ folderId, diagramId, initial, queryClient,
      submit: (intent, signal) => save({ intent, signal }),
      prime: primeProjection,
      transition: (head, action, signal, restoreChangeSetId) => transition({ head, action, signal, restoreChangeSetId }),
    });
    return { id: original.id, name: original.name, timestamp: original.timestamp,
      historyRecoveryId: port.historyRecoveryId,
      mutationDestination: 'protected_plan', execute: port.execute, undo: port.undo, redo: port.redo };
  }, [diagramId, folderId, input.planning, loadModel, primeProjection, queryClient, save, transition]);
}
